import { memo, useMemo } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Typography from 'components/UI/Typography/Typography';

import { ReactComponent as DashedLineLeftBottom } from 'images/RewardSystem/CircularProgress/DashedLineLeftBottom.svg';
import { ReactComponent as DashedLineLeftTop } from 'images/RewardSystem/CircularProgress/DashedLineLeftTop.svg';
import { ReactComponent as DashedLineRight } from 'images/RewardSystem/CircularProgress/DashedLineRight.svg';
import CoinIcon from 'images/RewardSystem/coin.png';

import styles from './CircularProgress.module.scss';

const CircularProgress = ({
  value = 0,
  size = 220,
  strokeWidth = 48,
  companyLogo,
}) => {
  const position = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);

  const filled = value >= 100;
  const color = filled ? 'green' : value ? 'blue' : 'grey';

  const coinsJSX = useMemo(
    () =>
      Array(3)
        .fill(null)
        .map((_, index) => (
          <img
            key={index}
            src={CoinIcon}
            alt="coin icon"
            className={styles.coinIcon}
          />
        )),
    [],
  );

  const linesJSX = useMemo(
    () =>
      [DashedLineLeftTop, DashedLineLeftBottom, DashedLineRight].map(
        (Icon, index) => <Icon key={index} className={styles.line} />,
      ),
    [],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.circle}>
        <svg width={size} height={size} className={styles.progress}>
          <circle
            cx={position}
            cy={position}
            r={radius}
            strokeWidth={strokeWidth}
            className={styles.progressBar}
          />
          <circle
            cx={position}
            cy={position}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={classNames(styles.progressValue, {
              [styles.filled]: filled,
            })}
          />
        </svg>
        <img src={companyLogo} alt="company logo" className={styles.logo} />
      </div>
      <Typography weight="medium" color={color} className={styles.percentage}>
        {value.toFixed()}%
      </Typography>
      <div>{coinsJSX}</div>
      <div>{linesJSX}</div>
    </div>
  );
};

CircularProgress.propTypes = {
  value: PropTypes.number.isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  companyLogo: PropTypes.string.isRequired,
};

export default memo(CircularProgress);
